import * as RDialog from "@radix-ui/react-dialog";
import cx from "classnames";
import {
  createContext,
  forwardRef,
  HTMLAttributes,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import CloseIcon from "static/images/Close.svg";
import { Button } from "../Button/Button";
import { Slot } from "@radix-ui/react-slot";

enum ModalSize {
  /* @tw */
  small = "t-w-[440px]",
  /* @tw */
  regular = "t-w-[560px]",
  /* @tw */
  large = "t-w-[680px]",
  /* @tw */
  xl = "t-w-[960px]",
  /* @tw */
  xxl = "t-w-[1140px]",
  /* @tw */
  xxxl = "t-w-screen",
  /* @tw */
  fullscreen = "t-w-full t-h-full",
}

type ModalProps = {
  asChild?: boolean;
  children?: ReactNode;
  size?: keyof typeof ModalSize;
  useCustomOverlay?: boolean;
  overlayClassName?: string;
  className?: string;
} & RDialog.DialogContentProps;

const preventClose = (e: React.KeyboardEvent<HTMLDivElement>) => {
  if (e.key === " " || e.key === "Spacebar" || e.key === "Enter") {
    e.preventDefault();
  }
};

const ModalContext = createContext<{
  hasFooter: boolean;
  setHasFooter: (hasFooter: boolean) => void;
}>({
  hasFooter: false,
  setHasFooter: () => {},
});

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [hasFooter, setHasFooter] = useState(false);

  return (
    <ModalContext.Provider value={{ hasFooter, setHasFooter }}>
      {children}
    </ModalContext.Provider>
  );
};

const Root = (props: RDialog.DialogProps) => {
  return (
    <RDialog.Root {...props}>
      <ModalProvider>{props.children}</ModalProvider>
    </RDialog.Root>
  );
};

const Header = (props: HTMLAttributes<HTMLDivElement>) => {
  return (
    <div
      {...props}
      className={cx(
        "t-flex t-flex-shrink-0 t-items-center t-justify-between t-border-0 t-border-b t-border-solid t-border-b-neutral-0 t-py-4 t-pl-8 t-pr-4 sm:t-pl-3",
        props.className || ""
      )}
    />
  );
};

const Title = (
  props: RDialog.DialogTitleProps &
    React.RefAttributes<HTMLHeadingElement> & { titleIcon?: React.JSX.Element }
) => {
  return (
    <RDialog.Title
      asChild
      className={cx("t-mb-0 t-text-subtext", props.className || "")}
      {...props}
    >
      <span className="t-flex t-justify-between">
        {props?.children}
        {props?.titleIcon}
      </span>
    </RDialog.Title>
  );
};

const Subtitle = (props: RDialog.DialogDescriptionProps) => {
  return (
    <RDialog.Description
      {...props}
      className={cx(
        "t-mb-0 t-mt-1 t-text-subtext-sm t-text-neutral-30",
        props.className || ""
      )}
    />
  );
};

const Close = (props: RDialog.DialogCloseProps) => (
  <RDialog.Close {...props} asChild>
    <Button
      customType="ghost_icon"
      size="small"
      title="Close Modal"
      type="button"
    >
      <img className="t-h-4 t-w-4" src={CloseIcon} alt="Close Modal" />
    </Button>
  </RDialog.Close>
);

type BodyProps = {
  asChild?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const Body: React.FC<BodyProps> = ({ asChild, ...props }) => {
  const { hasFooter } = useContext(ModalContext);
  const Component = asChild ? Slot : "div";

  if (asChild) {
    return <Component {...props} />;
  }

  return (
    <Component
      {...props}
      className={cx(
        "t-overflow-auto t-px-8 sm:t-px-3",
        {
          "t-py-5": hasFooter,
          "t-pt-5 t-pb-8": !hasFooter,
        },
        props.className || ""
      )}
    />
  );
};

const Footer = (props: HTMLAttributes<HTMLDivElement>) => {
  const { setHasFooter } = useContext(ModalContext);

  useEffect(() => {
    setHasFooter(true);
    return () => setHasFooter(false);
  }, [setHasFooter]);
  return (
    <div
      {...props}
      className={cx(
        "t-flex-shrink-0 t-px-6 sm:t-px-3 t-py-4 t-border-0 t-border-t t-border-solid t-border-t-neutral-0",
        props.className || ""
      )}
    />
  );
};

const Content = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      // The default overlay removed scroll from the elements which renders in the portal (Body)
      useCustomOverlay = false,
      size = "regular",
      className = "",
      overlayClassName = "",
      ...rest
    }: ModalProps,
    forwardedRef
  ) => {
    return (
      <RDialog.Portal>
        {useCustomOverlay ? (
          <CustomOverlay className={overlayClassName} />
        ) : (
          <Overlay className={overlayClassName} />
        )}
        <RDialog.Content
          {...rest}
          data-modal="true"
          className={cx(
            "t-fixed t-left-1/2 t-top-1/2 t-z-modal t-flex -t-translate-x-1/2 -t-translate-y-1/2 t-flex-col t-bg-white ",
            ModalSize[size],
            className,
            {
              "t-rounded-lg t-max-h-[90vh] t-max-w-[90vw] t-container":
                size !== "fullscreen",
            }
          )}
        >
          {children}
        </RDialog.Content>
      </RDialog.Portal>
    );
  }
);

let customOverlayCount = 0;

const CustomOverlay = (
  props: RDialog.DialogOverlayProps & React.RefAttributes<HTMLDivElement>
) => {
  useEffect(() => {
    customOverlayCount += 1;
    document.body.classList.add("pointer-events-none");

    return () => {
      customOverlayCount = Math.max(0, customOverlayCount - 1);
      if (customOverlayCount === 0) {
        document.body.classList.remove("pointer-events-none");
        document.body.style.removeProperty("pointer-events");
      }
    };
  }, []);

  return (
    <div
      className={cx(
        "t-fixed t-inset-0 t-z-modal t-bg-text-100 t-opacity-20",
        props.className || ""
      )}
    />
  );
};

const FooterButtonGroup = ({ children }: { children: ReactNode }) => (
  <Footer>
    <div className="t-flex t-gap-2 t-items-center t-justify-end sm:t-flex-wrap">
      {children}
    </div>
  </Footer>
);

const Overlay = forwardRef(
  ({
    ...props
  }: RDialog.DialogOverlayProps & React.RefAttributes<HTMLDivElement>) => (
    <RDialog.Overlay
      {...props}
      className={cx(
        "t-fixed t-inset-0 t-z-modal t-bg-text-100 t-opacity-20",
        props.className || ""
      )}
    />
  )
);

const Modal = {
  ...RDialog,
  Header,
  Title,
  Subtitle,
  Close,
  Root,
  Body,
  Footer,
  FooterButtonGroup,
  Content,
  RawContent: RDialog.Content,
  RawClose: RDialog.Close,
  Overlay,
};

export default Modal;
